import React from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface NavigationProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}

export const Navigation: React.FC<NavigationProps> = ({ currentPage = 'prompts', onNavigate }) => {
  const { user, logout } = useAuth();

  const handleNavigation = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    { id: 'prompts', label: '提示词库', icon: '📝' },
    { id: 'trash', label: '回收站', icon: '🗑️' },
    { id: 'profile', label: '个人中心', icon: '👤' },
  ];

  // 如果是管理员，添加管理员入口
  const isAdmin = user?.role === 'admin';
  if (isAdmin) {
    menuItems.push({ id: 'admin', label: '管理后台', icon: '👑' });
  }

  const handleMenuClick = (itemId: string) => {
    if (itemId === 'admin') {
      // 直接跳转到管理员页面
      window.location.href = '/admin';
    } else {
      handleNavigation(itemId);
    }
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo和品牌 */}
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <h1 className="text-xl font-bold text-blue-600">PromptMaster</h1>
              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                v0.8
              </span>
            </div>
          </div>

          {/* 主导航菜单 */}
          <div className="hidden md:flex items-center space-x-8">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentPage === item.id || (item.id === 'prompts' && (currentPage === 'create-prompt' || currentPage === 'dashboard'))
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* 用户菜单 */}
          <div className="flex items-center space-x-4">
            {/* 用户信息 */}
            <div className="hidden md:flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-medium">
                    {user?.email.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="hidden lg:block">{user?.email}</span>
              </div>
            </div>

            {/* 退出按钮 */}
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden md:block">退出</span>
            </button>
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      <div className="md:hidden">
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-50">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item.id)}
              className={`flex items-center space-x-3 w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors ${
                currentPage === item.id || (item.id === 'prompts' && (currentPage === 'create-prompt' || currentPage === 'dashboard'))
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
};